import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AccountType, UserProfile } from "@preveil-api";
import { DefaultRoutes, PublicRoutes, LocalAccountStorage, Account, useAppDispatch, account_types, AppConfiguration, AuthContext } from "src/common";
import { accountActions, uiActions } from "src/store";

// Export the provider as we need to wrap the entire app with it
type AllProps = {
  children?: React.ReactNode;
}

export function AuthContextProvider(props: AllProps) {
  const { children } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<UserProfile | undefined>();
  const [redirect_path, setRedirectPath] = useState<string>(DefaultRoutes.mail_default);

  // Description: Use loginComplete Callback for all Forms that logs users in 
  // Sets LocalStorage and Session Storage and redirect information
  function loginComplete(current_account: Account, account_type: AccountType = account_types.no_user, redirect_url?: string, disable_navigate: boolean = false) {
    let url: string = PublicRoutes.get_started_route;
    if (!!current_account) {
      // If the account is pending express, redirect to the create account page with the current account to setup password, 2FA and downgrade to express account
      if (current_account.pending_account_type === account_types.express) {
        url = PublicRoutes.create_account_route;
      } else {
        const profile = Account.getAccountProfile(current_account);
        (!!current_account.org_info) ?
          dispatch(accountActions.getOrganizationInfo({ account_ids: Account.getAccountIdentifiers(current_account), id: current_account.org_info?.org_id, org_info: current_account.org_info })) :
          AppConfiguration.buildForApp() && dispatch(accountActions.setGlobalLocalSync(true)); // Set this to true for non org users
        setProfile(profile);
        const storage = new LocalAccountStorage(current_account, account_type);
        storage.setLocalStorageSessions();
        url = redirect_url || redirect_path || DefaultRoutes.mail_default;
      }
    } else {
      url = redirect_url || PublicRoutes.get_started_route;
      setRedirectPath(url);
    }
    // Allow Login component to handle navigation if there are core errors
    if (!disable_navigate) {
      dispatch(uiActions.handleSetRedirectUrl()); // Reset Redirect in state
      navigate(url);
    }
  }

  // Only get re-renders when logging in. Make the provider update only when it should, only re-renders if the profile, loading or error states change.
  // Whenever the `value` passed into a provider changes, the whole tree under the provider re-renders.
  const memoed_value = useMemo(
    () => ({
      profile,
      redirectPath: redirect_path,
      setRedirectPath,
      setProfile,
      loginComplete
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profile, redirect_path]
  );

  // We only want to render the underlying app after we get current profile
  return (
    <AuthContext.Provider value={memoed_value}>
      {children}
    </AuthContext.Provider>
  );
}

